import { useState } from 'react';
import { Loader2 } from 'lucide-react';
import Reveal from './ui/Reveal';
import Icon, { GithubIcon, LinkedinIcon } from './ui/Icon';
import { emailjs as emailjsConfig, projectTypes, site } from '../data/content';

const fieldClass =
  'mt-2 block w-full rounded-xl border border-white/12 bg-ink-900 px-4 py-3 text-[15px] text-fg placeholder:text-subtle transition-colors hover:border-white/20 focus:border-indigo-300/60 focus:outline-none focus:ring-2 focus:ring-indigo-300/25';

const labelClass = 'block text-sm font-medium text-fg';

const emptyForm = { user_name: '', user_email: '', project_type: '', message: '' };

const ContactMethod = ({ icon, label, children }) => (
  <li className="flex items-center gap-4">
    <span className="grid h-11 w-11 shrink-0 place-items-center rounded-xl border border-white/10 bg-white/[0.04] text-indigo-300">
      {icon}
    </span>
    <div className="min-w-0">
      <p className="font-mono text-[11px] uppercase tracking-[0.14em] text-subtle">{label}</p>
      <div className="mt-0.5 truncate text-[15px] font-medium text-fg">{children}</div>
    </div>
  </li>
);

const Contact = () => {
  const [form, setForm] = useState(emptyForm);
  const [honeypot, setHoneypot] = useState('');
  const [status, setStatus] = useState('idle'); // idle | sending | sent | error

  const onChange = (e) => setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (status === 'sending') return;

    // Bots fill hidden fields; pretend success without sending anything.
    if (honeypot) {
      setStatus('sent');
      return;
    }

    setStatus('sending');
    try {
      const emailjs = (await import('emailjs-com')).default;
      const params = {
        user_name: form.user_name,
        user_email: form.user_email,
        message: form.project_type ? `Project type: ${form.project_type}\n\n${form.message}` : form.message,
      };
      await emailjs.send(emailjsConfig.service, emailjsConfig.template, params, emailjsConfig.publicKey);
      setStatus('sent');
    } catch {
      setStatus('error');
    }
  };

  const reset = () => {
    setForm(emptyForm);
    setStatus('idle');
  };

  return (
    <section id="contact" className="section border-t border-white/[0.06]" aria-labelledby="contact-title">
      <div
        className="pointer-events-none absolute inset-x-0 bottom-0 h-[560px]"
        style={{ background: 'radial-gradient(60% 100% at 50% 100%, rgba(99,102,241,0.18), transparent 70%)' }}
        aria-hidden="true"
      />
      <div className="container-page relative">
        <div className="grid gap-12 lg:grid-cols-[0.95fr_1.05fr] lg:gap-16">
          <Reveal>
            <p className="eyebrow">Contact</p>
            <h2 id="contact-title" className="section-title mt-3">
              Have a project in mind?
            </h2>
            <p className="section-lead">
              Let’s build something that works for your business. Tell me what you’re trying to achieve and I’ll reply
              within 24 hours with honest advice and a free estimate.
            </p>

            <ul className="mt-9 space-y-5">
              <ContactMethod icon={<Icon name="Mail" className="h-5 w-5" />} label="Email">
                <a href={`mailto:${site.email}`} className="underline-offset-4 hover:underline">
                  {site.email}
                </a>
              </ContactMethod>
              <ContactMethod icon={<Icon name="MapPin" className="h-5 w-5" />} label="Location">
                {site.location}
              </ContactMethod>
              <ContactMethod icon={<GithubIcon className="h-5 w-5" />} label="GitHub">
                <a href={site.github} target="_blank" rel="noopener noreferrer" className="underline-offset-4 hover:underline">
                  github.com/Mirwes-Hakimi
                  <span className="sr-only"> (opens in a new tab)</span>
                </a>
              </ContactMethod>
              <ContactMethod icon={<LinkedinIcon className="h-5 w-5" />} label="LinkedIn">
                <a href={site.linkedin} target="_blank" rel="noopener noreferrer" className="underline-offset-4 hover:underline">
                  Mirwes Hakimi
                  <span className="sr-only"> (opens in a new tab)</span>
                </a>
              </ContactMethod>
            </ul>
          </Reveal>

          <Reveal delay={100}>
            <div className="card p-6 sm:p-8">
              {status === 'sent' ? (
                <div role="status" className="flex flex-col items-start gap-4 py-6">
                  <span className="grid h-12 w-12 place-items-center rounded-full bg-emerald-400/15 text-emerald-300">
                    <Icon name="Check" className="h-6 w-6" strokeWidth={2.5} />
                  </span>
                  <h3 className="text-2xl font-semibold tracking-tight text-fg">Message sent</h3>
                  <p className="text-[15px] leading-relaxed text-muted">
                    Thanks for reaching out. I’ll get back to you within 24 hours.
                  </p>
                  <button
                    type="button"
                    onClick={reset}
                    className="text-sm font-medium text-indigo-300 underline-offset-4 hover:underline"
                  >
                    Send another message
                  </button>
                </div>
              ) : (
                <form onSubmit={onSubmit} className="space-y-5" aria-describedby="form-note">
                  <h3 className="text-xl font-semibold tracking-tight text-fg">Start a project</h3>

                  <div className="grid gap-5 sm:grid-cols-2">
                    <div>
                      <label htmlFor="user_name" className={labelClass}>
                        Name
                      </label>
                      <input
                        id="user_name"
                        name="user_name"
                        type="text"
                        required
                        autoComplete="name"
                        value={form.user_name}
                        onChange={onChange}
                        placeholder="Your name"
                        className={fieldClass}
                      />
                    </div>
                    <div>
                      <label htmlFor="user_email" className={labelClass}>
                        Email
                      </label>
                      <input
                        id="user_email"
                        name="user_email"
                        type="email"
                        required
                        autoComplete="email"
                        value={form.user_email}
                        onChange={onChange}
                        placeholder="you@company.com"
                        className={fieldClass}
                      />
                    </div>
                  </div>

                  <div>
                    <label htmlFor="project_type" className={labelClass}>
                      What do you need? <span className="font-normal text-subtle">(optional)</span>
                    </label>
                    <select
                      id="project_type"
                      name="project_type"
                      value={form.project_type}
                      onChange={onChange}
                      className={`${fieldClass} appearance-none bg-[right_1rem_center] bg-no-repeat pr-10`}
                      style={{
                        backgroundImage:
                          "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='%238a90aa' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpath d='m6 9 6 6 6-6'/%3E%3C/svg%3E\")",
                      }}
                    >
                      <option value="">Select a project type</option>
                      {projectTypes.map((type) => (
                        <option key={type} value={type}>
                          {type}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label htmlFor="message" className={labelClass}>
                      Tell me about your project
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      required
                      rows={5}
                      value={form.message}
                      onChange={onChange}
                      placeholder="What are you building, and what problem are you solving?"
                      className={`${fieldClass} resize-y`}
                    />
                  </div>

                  {/* Honeypot: hidden from people and assistive tech, visible to bots. */}
                  <div className="absolute -left-[9999px] h-0 w-0 overflow-hidden" aria-hidden="true">
                    <label htmlFor="company">Company</label>
                    <input
                      id="company"
                      name="company"
                      type="text"
                      tabIndex={-1}
                      autoComplete="off"
                      value={honeypot}
                      onChange={(e) => setHoneypot(e.target.value)}
                    />
                  </div>

                  <button
                    type="submit"
                    disabled={status === 'sending'}
                    className="group inline-flex h-12 w-full items-center justify-center gap-2 rounded-full bg-white px-7 text-[15px] font-semibold text-ink-950 shadow-[0_8px_30px_-10px_rgba(165,180,252,0.6)] transition-all duration-200 hover:bg-indigo-100 disabled:cursor-not-allowed disabled:opacity-70"
                  >
                    {status === 'sending' ? (
                      <>
                        <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
                        Sending…
                      </>
                    ) : (
                      <>
                        Start a Project
                        <Icon
                          name="ArrowRight"
                          className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-0.5"
                          strokeWidth={2.25}
                        />
                      </>
                    )}
                  </button>

                  <div aria-live="polite">
                    {status === 'error' && (
                      <p className="rounded-xl border border-red-400/30 bg-red-400/10 px-4 py-3 text-sm text-red-200">
                        Something went wrong sending your message. Please email me directly at{' '}
                        <a href={`mailto:${site.email}`} className="font-semibold underline underline-offset-4">
                          {site.email}
                        </a>
                        .
                      </p>
                    )}
                  </div>

                  <p id="form-note" className="text-xs leading-relaxed text-subtle">
                    Free estimate, no obligation. Your details are only used to reply to your inquiry.
                  </p>
                </form>
              )}
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
};

export default Contact;
